import { PropertyAnimation } from "@spi/Common/Animation/PropertyAnimation";
import { Context } from "../Engine";
import { IDrawableObject, ObjectInteraction, RenderSettings, RenderMode } from "./ObjectManager";
import { VertexBuffer } from "./VertexBuffer";
import { mat4 } from "gl-matrix";
import { toRadian } from "@spi/Common/Math/math";
import { Camera } from "../View/Camera";

export class SkyBox implements IDrawableObject {

    order: number = 0;
    animatedProperties: PropertyAnimation;
    interaction: ObjectInteraction;
    private buffer: VertexBuffer;

    public cubeTexture: { glTexture: WebGLTexture };

    constructor(private ctx: Context, public rotationSource: RotationSource) {
        this.createGeometry();
    }

    public render(ctx: Context, camera: Camera, settings: RenderSettings) {

        let vertexBuffer = this.buffer;

        if (vertexBuffer.itemCount == 0) {
            return;
        }

        let gl = ctx.gl;

        let shader = ctx.engine.shaderManager.getSkyBoxShader();
        ctx.setShader(shader);

        gl.useProgram(shader.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer.glVertexBuffer);
        gl.vertexAttribPointer(shader.attributes.aPosition, 3, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * 3, 0);


        let mv = mat4.create();

        mat4.rotateY(mv, mv, toRadian(this.rotationSource.rotation));

        gl.uniformMatrix4fv(shader.uniforms.uProjection, false, camera.projectionMatrix);
        gl.uniformMatrix4fv(shader.uniforms.uView, false, mv);
        gl.uniform1i(shader.uniforms.uSkyboxSampler, 0);

        gl.depthMask(false);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_CUBE_MAP, this.cubeTexture.glTexture);
        gl.drawArrays(vertexBuffer.glDrawMode, 0, vertexBuffer.itemCount);

        gl.depthMask(true);
    }

    private createGeometry() {
        let gl = this.ctx.gl;

        let skyboxVertices = [
            -1.0, 1.0, -1.0,
            -1.0, -1.0, -1.0,
            1.0, -1.0, -1.0,
            1.0, -1.0, -1.0,
            1.0, 1.0, -1.0,
            -1.0, 1.0, -1.0,

            -1.0, -1.0, 1.0,
            -1.0, -1.0, -1.0,
            -1.0, 1.0, -1.0,
            -1.0, 1.0, -1.0,
            -1.0, 1.0, 1.0,
            -1.0, -1.0, 1.0,

            1.0, -1.0, -1.0,
            1.0, -1.0, 1.0,
            1.0, 1.0, 1.0,
            1.0, 1.0, 1.0,
            1.0, 1.0, -1.0,
            1.0, -1.0, -1.0,

            -1.0, -1.0, 1.0,
            -1.0, 1.0, 1.0,
            1.0, 1.0, 1.0,
            1.0, 1.0, 1.0,
            1.0, -1.0, 1.0,
            -1.0, -1.0, 1.0,

            -1.0, 1.0, -1.0,
            1.0, 1.0, -1.0,
            1.0, 1.0, 1.0,
            1.0, 1.0, 1.0,
            -1.0, 1.0, 1.0,
            -1.0, 1.0, -1.0,

            -1.0, -1.0, -1.0,
            -1.0, -1.0, 1.0,
            1.0, -1.0, -1.0,
            1.0, -1.0, -1.0,
            -1.0, -1.0, 1.0,
            1.0, -1.0, 1.0
        ];


        this.buffer = new VertexBuffer(gl.createBuffer(), gl.TRIANGLES, 0, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(skyboxVertices), gl.STATIC_DRAW);

        this.buffer.itemSize = 3;
        this.buffer.itemCount = skyboxVertices.length / 3;
    }
}

type RotationSource = {
    rotation: number
};